import type {
  CabinetAppointmentView,
  CabinetWarningCode,
  CreateBusyBlockRequest,
} from "@planerius/contracts/cabinet"
import { useCallback, useMemo, useRef, useState } from "react"

import { revealClientPhone } from "../../api/clients.api"
import {
  cancelAppointment,
  confirmAppointment,
  createBusyBlock,
  markAppointmentArrived,
  markAppointmentCompleted,
  markAppointmentNoShow,
  rescheduleAppointment,
  undoAppointmentNoShow,
} from "../../api/journal-actions.api"
import { accessStore } from "../../access/access-store"
import type { ApiProblem } from "../../api/problem"
import { isApiProblem, transportProblem } from "../../api/problem"
import type { CancelSubmit, NoShowSubmit } from "../../journal/appointment-dialogs"
import type { AppointmentAction } from "../../journal/idempotency-book"
import { appointmentIntent, createIdempotencyBook } from "../../journal/idempotency-book"

/**
 * Действия панели записи: приход, завершение, неявка и её снятие, отмена, перенос,
 * показ телефона.
 *
 * ЗДЕСЬ СОБРАНЫ ТРИ ПРАВИЛА, КОТОРЫЕ НЕЛЬЗЯ РАЗМАЗЫВАТЬ ПО КНОПКАМ.
 *
 * 1. КЛЮЧ ИДЕМПОТЕНТНОСТИ ЖИВЁТ ДОЛЬШЕ ЗАПРОСА. Повтор после обрыва обязан попасть
 *    в тот же ключ, иначе «Клиент пришёл» превращается в две отметки, а «Записать» —
 *    во второй визит. Поэтому ключ хранится в книге намерений и забывается ТОЛЬКО
 *    после успеха или после отказа, который повторять бессмысленно. Отказ связи ключ
 *    не трогает — следующая попытка обязана быть тем же самым действием.
 *
 * 2. ДЕЙСТВИЕ ПРИМЕНЯЕТСЯ К ТОЙ ВЕРСИИ, КОТОРУЮ ВИДЕЛ ЧЕЛОВЕК. expectedUpdatedAt берётся
 *    из открытой карточки, а не из свежего ответа сервера: иначе «Отменить» молча
 *    отменит запись, которую соседний администратор только что перенёс на другое время.
 *
 * 3. ПРЕДУПРЕЖДЕНИЕ — ЭТО ВОПРОС, А НЕ ОШИБКА. 422 confirmation_required означает, что
 *    сервер согласен выполнить действие, но хочет, чтобы человек прочитал последствия.
 *    Текст пишет сервер (правила знает он), а кабинет обязан повторить запрос ровно
 *    с теми кодами, которые показал.
 */

/** Запись, которую человек видел в момент нажатия. Нужна для конфликта версий. */
export interface VersionConflict {
  readonly action: AppointmentAction
  readonly snapshot: CabinetAppointmentView
  readonly changedBy: string | null
  readonly currentUpdatedAt: string | null
  /** Подпись повторного действия: «Всё равно отменить». */
  readonly repeatLabel: string
  /** Повторить то же действие, но уже с новой версией записи. */
  readonly repeat: (version: string) => void
}

/** Сервер просит подтвердить последствия. Показывается диалогом, а не тихой ошибкой. */
export interface PendingConfirmation {
  readonly messages: readonly string[]
  readonly confirm: () => void
}

export interface JournalActionsState {
  /** Какое действие выполняется сейчас: его кнопка показывает «Сохраняем…». */
  readonly busy: AppointmentAction | "reveal" | "block" | null
  readonly failure: string | null
  readonly conflict: VersionConflict | null
  readonly confirmation: PendingConfirmation | null
  /** Открытый телефон клиента: id клиента → номер. Живёт до перезагрузки экрана. */
  readonly revealed: Readonly<Record<string, string>>
}

export interface JournalActions {
  readonly state: JournalActionsState
  /**
   * «Заблокировать время» — закрыть окно без записи (перерыв, уход мастера).
   *
   * Отдельно от действий над записью: у блокировки нет версии строки, потому что
   * нет и самой строки — её только создают. Ключ идемпотентности всё равно обязателен:
   * двойное нажатие на телефоне не редкость, а второй блок поверх первого сервер
   * отверг бы «это время уже занято» на действие, которое человек только что выполнил.
   */
  readonly blockTime: (request: CreateBusyBlockRequest) => void
  readonly confirm: (appointment: CabinetAppointmentView) => void
  readonly arrived: (appointment: CabinetAppointmentView) => void
  readonly completed: (appointment: CabinetAppointmentView) => void
  readonly noShow: (appointment: CabinetAppointmentView, input: NoShowSubmit) => void
  readonly undoNoShow: (appointment: CabinetAppointmentView) => void
  readonly cancel: (appointment: CabinetAppointmentView, input: CancelSubmit) => void
  readonly reschedule: (
    appointment: CabinetAppointmentView,
    input: {
      readonly startAt: string
      readonly employeeId: string | null
      readonly initiatedBy: "client" | "staff"
      readonly notifyClient: boolean
    },
  ) => void
  readonly revealPhone: (appointment: CabinetAppointmentView) => void
  readonly dismissFailure: () => void
  readonly dismissConflict: () => void
  readonly dismissConfirmation: () => void
}

const REPEAT_LABEL: Readonly<Record<AppointmentAction, string>> = {
  confirm: "Всё равно подтвердить",
  arrived: "Всё равно отметить приход",
  completed: "Всё равно завершить",
  "no-show": "Всё равно отметить неявку",
  "no-show-undo": "Всё равно снять отметку",
  cancel: "Всё равно отменить",
  reschedule: "Всё равно перенести",
  update: "Всё равно сохранить",
}

/** Отказ словами. Сервер присылает detail — он точнее любого нашего пересказа. */
export function failureText(problem: ApiProblem): string {
  if (problem.code === "permission_denied") {
    // ПРАВА МОГЛИ ОТОБРАТЬ, ПОКА ВКЛАДКА БЫЛА ОТКРЫТА (1-Э1.11). Тогда сообщение
    // «у вашей роли нет права» звучит как «программа сломалась»: полчаса назад право
    // было. Профиль перечитывается тут же, и человек видит свой настоящий доступ —
    // а введённое остаётся на экране, потому что ничего не перезагружается.
    void accessStore.refresh()
    return "Доступ к этому действию закрыли, пока страница была открыта. Мы обновили ваши права — введённое сохранено."
  }
  if (problem.detail !== null && problem.detail.length > 0) return problem.detail
  if (problem.code === "impersonation_read_only") {
    return "Режим поддержки открыт только для чтения: изменения недоступны."
  }
  if (problem.retryable) {
    return `${problem.title}. Повторите — введённое сохранено.`
  }
  return problem.title
}

/**
 * Тип выписан отдельно не для красоты: обработчик вызывает сам себя (повтор после
 * конфликта версий и после подтверждения предупреждений), а у рекурсивной функции
 * без явного типа вывод типов замыкается на себя и падает.
 */
type RunAction = (
  action: AppointmentAction,
  appointment: CabinetAppointmentView,
  send: (
    key: string,
    acknowledged: readonly CabinetWarningCode[],
    version: string,
  ) => Promise<unknown>,
  version?: string,
  acknowledged?: readonly CabinetWarningCode[],
) => void

export function useJournalActions(options: { readonly onChanged: () => void }): JournalActions {
  const bookRef = useRef(createIdempotencyBook())
  const [state, setState] = useState<JournalActionsState>({
    busy: null,
    failure: null,
    conflict: null,
    confirmation: null,
    revealed: {},
  })

  const { onChanged } = options

  /**
   * Блокировка времени. Путь короче, чем у действий над записью: конфликта версий
   * здесь быть не может, а предупреждения («внутри окна есть записи», «вне смены»)
   * подтверждаются так же — повтором того же запроса с теми же кодами.
   */
  const runBlock = useCallback(
    (request: CreateBusyBlockRequest, acknowledged: readonly CabinetWarningCode[] = []): void => {
      const intent = `busy:${request.branchId}:${request.employeeId ?? request.resourceId ?? ""}:${request.startAt}:${request.endAt}`
      const key = bookRef.current.keyFor(intent)
      setState((previous) => ({ ...previous, busy: "block", failure: null }))

      void (async () => {
        try {
          await createBusyBlock({ ...request, acknowledgedWarnings: [...acknowledged] }, key)
          bookRef.current.forget(intent)
          setState((previous) => ({ ...previous, busy: null, failure: null, confirmation: null }))
          onChanged()
        } catch (error) {
          const problem = isApiProblem(error) ? error : transportProblem("unknown")
          if (!problem.retryable) bookRef.current.forget(intent)

          if (problem.code === "confirmation_required" && problem.warnings.length > 0) {
            const codes = problem.warnings.map((warning) => warning.code)
            setState((previous) => ({
              ...previous,
              busy: null,
              confirmation: {
                messages: problem.warnings.map((warning) => warning.message),
                confirm: () => {
                  runBlock(request, codes)
                },
              },
            }))
            return
          }

          setState((previous) => ({ ...previous, busy: null, failure: failureText(problem) }))
        }
      })()
    },
    [onChanged],
  )

  /**
   * Один путь для всех действий панели. Отличаются они только телом запроса, а всё
   * остальное — ключ, версия, разбор отказа — обязано быть одинаковым: разное поведение
   * «Отменить» и «Не пришёл» при обрыве связи было бы не разнообразием, а ошибкой.
   */
  const run = useCallback<RunAction>(
    (action, appointment, send, version = appointment.updatedAt, acknowledged = []): void => {
      const intent = appointmentIntent(action, appointment.id, version)
      const key = bookRef.current.keyFor(intent)

      setState((previous) => ({ ...previous, busy: action, failure: null }))

      void (async () => {
        try {
          await send(key, acknowledged, version)
          bookRef.current.forget(intent)
          setState((previous) => ({
            ...previous,
            busy: null,
            failure: null,
            conflict: null,
            confirmation: null,
          }))
          onChanged()
        } catch (error) {
          const problem = isApiProblem(error) ? error : transportProblem("unknown")

          // Ключ НЕ забывается только у отказов, которые имеет смысл повторить тем же
          // действием: обрыв связи, таймаут, пятисотка. Всё остальное — новое намерение.
          if (!problem.retryable) bookRef.current.forget(intent)

          if (problem.code === "version_conflict") {
            setState((previous) => ({
              ...previous,
              busy: null,
              failure: null,
              conflict: {
                action,
                snapshot: appointment,
                changedBy: problem.changedBy,
                currentUpdatedAt: problem.currentUpdatedAt,
                repeatLabel: REPEAT_LABEL[action],
                repeat: (fresh: string) => {
                  run(action, appointment, send, fresh, acknowledged)
                },
              },
            }))
            // День перечитывается сразу: человеку показывают, что стало, а не только то,
            // что его действие не прошло.
            onChanged()
            return
          }

          if (problem.code === "confirmation_required" && problem.warnings.length > 0) {
            const codes = problem.warnings.map((warning) => warning.code)
            setState((previous) => ({
              ...previous,
              busy: null,
              confirmation: {
                messages: problem.warnings.map((warning) => warning.message),
                confirm: () => {
                  run(action, appointment, send, version, codes)
                },
              },
            }))
            return
          }

          setState((previous) => ({ ...previous, busy: null, failure: failureText(problem) }))
        }
      })()
    },
    [onChanged],
  )

  return useMemo<JournalActions>(
    () => ({
      state,
      blockTime: runBlock,
      confirm: (appointment) => {
        run("confirm", appointment, (key, _ack, version) =>
          confirmAppointment({ appointmentId: appointment.id, expectedUpdatedAt: version }, key),
        )
      },
      arrived: (appointment) => {
        run("arrived", appointment, (key, _ack, version) =>
          markAppointmentArrived(
            { appointmentId: appointment.id, expectedUpdatedAt: version },
            key,
          ),
        )
      },
      completed: (appointment) => {
        run("completed", appointment, (key, ack, version) =>
          markAppointmentCompleted(
            {
              appointmentId: appointment.id,
              expectedUpdatedAt: version,
              acknowledgedWarnings: [...ack],
            },
            key,
          ),
        )
      },
      noShow: (appointment, input) => {
        run("no-show", appointment, (key, _ack, version) =>
          markAppointmentNoShow(
            {
              appointmentId: appointment.id,
              expectedUpdatedAt: version,
              ...(input.comment.trim() === "" ? {} : { comment: input.comment.trim() }),
              ...(input.restrictOnlineBooking
                ? {
                    blacklist: {
                      reason: input.comment.trim(),
                      blocksOnlineBooking: true,
                      expiresAt: null,
                    },
                  }
                : {}),
            },
            key,
          ),
        )
      },
      undoNoShow: (appointment) => {
        run("no-show-undo", appointment, (key, _ack, version) =>
          undoAppointmentNoShow(
            { appointmentId: appointment.id, expectedUpdatedAt: version },
            key,
          ),
        )
      },
      cancel: (appointment, input) => {
        run("cancel", appointment, (key, _ack, version) =>
          cancelAppointment(
            {
              appointmentId: appointment.id,
              initiatedBy: input.initiatedBy,
              reasonCode: input.reasonCode,
              notifyClient: input.notifyClient,
              expectedUpdatedAt: version,
              ...(input.comment.trim() === "" ? {} : { comment: input.comment.trim() }),
            },
            key,
          ),
        )
      },
      reschedule: (appointment, input) => {
        run("reschedule", appointment, (key, ack, version) =>
          rescheduleAppointment(
            {
              appointmentId: appointment.id,
              startAt: input.startAt,
              initiatedBy: input.initiatedBy,
              notifyClient: input.notifyClient,
              expectedUpdatedAt: version,
              acknowledgedWarnings: [...ack],
              ...(input.employeeId === null ? {} : { employeeId: input.employeeId }),
            },
            key,
          ),
        )
      },
      revealPhone: (appointment) => {
        setState((previous) => ({ ...previous, busy: "reveal", failure: null }))
        void (async () => {
          try {
            const result = await revealClientPhone({
              clientId: appointment.client.id,
              appointmentId: appointment.id,
            })
            setState((previous) => ({
              ...previous,
              busy: null,
              revealed: { ...previous.revealed, [appointment.client.id]: result.phone },
            }))
          } catch (error) {
            const problem = isApiProblem(error) ? error : transportProblem("unknown")
            setState((previous) => ({
              ...previous,
              busy: null,
              failure:
                problem.code === "permission_denied"
                  ? "Открывать номера клиентов может администратор салона."
                  : failureText(problem),
            }))
          }
        })()
      },
      dismissFailure: () => {
        setState((previous) => ({ ...previous, failure: null }))
      },
      dismissConflict: () => {
        setState((previous) => ({ ...previous, conflict: null }))
      },
      dismissConfirmation: () => {
        setState((previous) => ({ ...previous, confirmation: null }))
      },
    }),
    [state, run, runBlock],
  )
}
